import { computed, onBeforeUnmount, onMounted, watch, type Ref } from 'vue'
import { useState } from '#imports'

export const APP_HEADER_DESKTOP_FALLBACK_HEIGHT = 75
export const APP_HEADER_MOBILE_FALLBACK_HEIGHT = 52

/**
 * The only source of truth for viewport-level header geometry.
 * Consumers that are outside the header's normal flow (for example Teleport
 * overlays) should use these values instead of owning a pixel offset.
 */
export function useAppHeaderMetrics(headerRef?: Ref<HTMLElement | null>) {
    const isDesktop = useState('app-header-is-desktop', () => false)
    const headerHeight = useState(
        'app-header-height',
        () => APP_HEADER_DESKTOP_FALLBACK_HEIGHT
    )
    let resizeObserver: ResizeObserver | null = null
    let mediaQuery: MediaQueryList | null = null

    const fallbackHeight = () =>
        isDesktop.value
            ? APP_HEADER_DESKTOP_FALLBACK_HEIGHT
            : APP_HEADER_MOBILE_FALLBACK_HEIGHT

    const applyMetrics = (height = fallbackHeight()) => {
        headerHeight.value = Math.max(Math.ceil(height), fallbackHeight())

        if (!import.meta.client) return

        const root = document.documentElement
        const value = `${headerHeight.value}px`
        root.style.setProperty('--app-header-height', value)
        root.style.setProperty('--app-header-bottom', value)
        root.style.setProperty('--app-content-height', `calc(100dvh - ${value})`)
    }

    const syncViewport = () => {
        isDesktop.value = Boolean(mediaQuery?.matches)
        applyMetrics(headerRef?.value?.getBoundingClientRect().height)
    }

    const observeHeader = (element: HTMLElement | null) => {
        resizeObserver?.disconnect()
        if (!element || !import.meta.client) return

        resizeObserver = new ResizeObserver(entries => {
            const borderBoxSize = entries[0]?.borderBoxSize
            const measuredHeight = borderBoxSize?.[0]?.blockSize
            applyMetrics(measuredHeight ?? element.getBoundingClientRect().height)
        })
        resizeObserver.observe(element)
        applyMetrics(element.getBoundingClientRect().height)
    }

    if (headerRef) {
        onMounted(() => {
            mediaQuery = window.matchMedia('(min-width: 1024px)')
            mediaQuery.addEventListener('change', syncViewport)
            syncViewport()
            observeHeader(headerRef.value)
        })

        watch(headerRef, observeHeader)

        onBeforeUnmount(() => {
            resizeObserver?.disconnect()
            mediaQuery?.removeEventListener('change', syncViewport)
            // Layout transitions can remove NewAppHeader entirely (for example
            // when entering the dedicated chat shell). Do not leak the last
            // route's measured/banner height into the next layout.
            isDesktop.value = Boolean(mediaQuery?.matches)
            applyMetrics()
        })
    }

    return {
        headerHeight,
        headerBottom: headerHeight,
        contentHeight: computed(() => `calc(100dvh - ${headerHeight.value}px)`),
        isDesktop,
    }
}
